import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadCsvMatrix, type CsvCell } from "@/lib/csv";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import { REPORT_CATEGORIES, REPORT_CATEGORY_I18N_KEY } from "@/lib/reportCatalog";
import type { ExportTask } from "@/pages/admin/reports/hooks/useReportCenter";

type RangeKey = "all" | "today" | "7d" | "30d";

const RANGES: { key: RangeKey; labelKey: string }[] = [
  { key: "all", labelKey: "reports.rangeAll" },
  { key: "today", labelKey: "reports.rangeToday" },
  { key: "7d", labelKey: "reports.range7d" },
  { key: "30d", labelKey: "reports.range30d" },
];

const CATEGORY_TONE: Record<string, string> = {
  许可证: "bg-primary-100 text-primary-700",
  激活码: "bg-accent-100 text-accent-700",
  客户: "bg-secondary-100 text-secondary-900",
  审计: "bg-primary-100 text-primary-700",
  监控: "bg-accent-100 text-accent-800",
  批量: "bg-secondary-100 text-secondary-900",
};

function withinRange(iso: string, range: RangeKey): boolean {
  if (range === "all") return true;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return false;
  if (range === "today") {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return time >= start.getTime();
  }
  const days = range === "7d" ? 7 : 30;
  return Date.now() - time <= days * 86400000;
}

function monthKey(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "unknown";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

interface MonthGroup {
  key: string;
  tasks: ExportTask[];
  rows: number;
}

interface ExportTaskHistoryProps {
  tasks: ExportTask[];
  loading: boolean;
}

// 导出任务历史：集中展示报表中心产生的每一次导出（谁 / 何时 / 导出哪个报表 / 多少行），
// 支持按分类、导出人、时间范围翻查，并默认「按月分组归档」，方便月度审计逐月回看。
export default function ExportTaskHistory({ tasks, loading }: ExportTaskHistoryProps) {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState<string>("all");
  const [exporter, setExporter] = useState<string>("all");
  const [range, setRange] = useState<RangeKey>("all");
  // 快捷筛选：一键只看「到期提醒推送」留痕，与其他导出任务区分开。
  const [quick, setQuick] = useState<"all" | "renewal">("all");
  const [collapsed, setCollapsed] = useState<string[]>([]);

  const catLabel = (value: string) =>
    REPORT_CATEGORY_I18N_KEY[value] ? t(REPORT_CATEGORY_I18N_KEY[value]) : value;

  const exporters = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach((task) => set.add(task.exported_by_name || ""));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [tasks]);

  const filtered = useMemo(
    () =>
      tasks.filter((task) => {
        if (quick === "renewal" && task.report_key !== "renewal-reminder-push") return false;
        if (category !== "all" && (task.category || "other") !== category) return false;
        if (exporter !== "all" && (task.exported_by_name || "") !== exporter) return false;
        return withinRange(task.created_at, range);
      }),
    [tasks, quick, category, exporter, range],
  );

  const totalRows = useMemo(() => filtered.reduce((sum, task) => sum + (task.row_count || 0), 0), [filtered]);
  const hasFilter = quick !== "all" || category !== "all" || exporter !== "all" || range !== "all";

  // 本月「到期提醒推送」留痕汇总：本月共推了几次、触达多少人，收口在标题下方的小汇总条。
  const renewalSummary = useMemo(() => {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const startMs = start.getTime();
    const items = tasks.filter(
      (task) => task.report_key === "renewal-reminder-push" && new Date(task.created_at).getTime() >= startMs,
    );
    const reached = items.reduce((sum, task) => sum + (task.row_count || 0), 0);
    return { count: items.length, reached };
  }, [tasks]);

  // 按月归档：filtered 已按时间倒序，逐条并入所属月份分组，天然保持「新月份在前」。
  const groups = useMemo<MonthGroup[]>(() => {
    const map = new Map<string, MonthGroup>();
    filtered.forEach((task) => {
      const key = monthKey(task.created_at);
      let group = map.get(key);
      if (!group) {
        group = { key, tasks: [], rows: 0 };
        map.set(key, group);
      }
      group.tasks.push(task);
      group.rows += task.row_count || 0;
    });
    return Array.from(map.values());
  }, [filtered]);

  const formatMonth = (key: string): string => {
    if (key === "unknown") return t("reports.unknown");
    const date = new Date(`${key}-01T00:00:00`);
    return date.toLocaleDateString(i18n.language?.startsWith("en") ? "en-US" : "zh-CN", {
      year: "numeric",
      month: "long",
    });
  };

  const allCollapsed = groups.length > 0 && groups.every((group) => collapsed.includes(group.key));

  const toggleMonth = (key: string) => {
    setCollapsed((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const toggleAllMonths = () => {
    setCollapsed(allCollapsed ? [] : groups.map((group) => group.key));
  };

  const resetFilters = () => {
    setQuick("all");
    setCategory("all");
    setExporter("all");
    setRange("all");
  };

  const downloadHistory = () => {
    if (filtered.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const summary: CsvCell[][] = [
      ["导出任务历史"],
      [`导出时间：${formatFullDate(new Date().toISOString())}`],
      [
        `筛选条件：分类 ${category === "all" ? "全部" : category} · 导出人 ${exporter === "all" ? "全部" : exporter} · 时间 ${t(RANGES.find((item) => item.key === range)?.labelKey ?? "reports.rangeAll")}`,
      ],
      [`记录数：${filtered.length} · 累计导出行数：${totalRows}`],
      [],
    ];
    const header = ["月份", "导出时间", "导出人", "报表名称", "时间窗口", "分类", "导出行数"];
    const rows: CsvCell[][] = filtered.map((task) => [
      formatMonth(monthKey(task.created_at)),
      formatFullDate(task.created_at),
      task.exported_by_name || "未知",
      task.report_name,
      task.window_label || "",
      task.category || "其他",
      task.row_count,
    ]);
    downloadCsvMatrix(`导出任务历史_${stamp}.csv`, [...summary, header, ...rows]);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-history-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("reports.historyTitle")}</h2>
            <p className="text-xs text-foreground-500">{t("reports.historySub")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-list-check-2" />
            </span>
            {t("reports.historyCount", { count: filtered.length, rows: totalRows })}
          </span>
          <button
            type="button"
            disabled={filtered.length === 0}
            onClick={downloadHistory}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            {t("reports.historyExport")}
          </button>
        </div>
      </div>

      {/* 本月推送频率小汇总：一点「只看推送留痕」之前，先一眼看清本月推了几次、触达多少人。 */}
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
        <span className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-send-plane-line text-xs" />
          </span>
          <span className="text-xs font-medium text-foreground-700">本月到期提醒推送</span>
        </span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-800">
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-notification-3-line" />
          </span>
          推送 {renewalSummary.count} 次
        </span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-medium text-primary-700">
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-user-voice-line" />
          </span>
          触达 {renewalSummary.reached} 人
        </span>
        <button
          type="button"
          onClick={() => setQuick("renewal")}
          className="ml-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-medium text-accent-700 transition-colors hover:bg-accent-50"
        >
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-filter-3-line" />
          </span>
          只看推送留痕
        </button>
      </div>

      <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1 self-start">
          <button
            type="button"
            onClick={() => setQuick("all")}
            aria-pressed={quick === "all"}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
              quick === "all" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
            }`}
          >
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-list-check-2" />
            </span>
            {t("reports.historyFilterAll")}
          </button>
          <button
            type="button"
            onClick={() => setQuick("renewal")}
            aria-pressed={quick === "renewal"}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
              quick === "renewal" ? "bg-accent-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
            }`}
          >
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-notification-3-line" />
            </span>
            {t("reports.historyFilterRenewal")}
          </button>
        </div>

        <label className="flex items-center gap-2 text-xs text-foreground-600">
          <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
            <i className="ri-price-tag-3-line" />
          </span>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className="h-9 min-w-[130px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
          >
            <option value="all">{t("reports.historyAllCategory")}</option>
            {REPORT_CATEGORIES.map((item) => (
              <option key={item} value={item}>
                {catLabel(item)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-xs text-foreground-600">
          <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
            <i className="ri-user-line" />
          </span>
          <select
            value={exporter}
            onChange={(event) => setExporter(event.target.value)}
            className="h-9 min-w-[140px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
          >
            <option value="all">{t("reports.historyAllExporter")}</option>
            {exporters.map((name) => (
              <option key={name} value={name}>
                {name || t("reports.unknown")}
              </option>
            ))}
          </select>
        </label>

        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {RANGES.map((item) => {
            const active = range === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setRange(item.key)}
                aria-pressed={active}
                className={`inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  active ? "bg-secondary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
                }`}
              >
                {t(item.labelKey)}
              </button>
            );
          })}
        </div>

        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-close-circle-line" />
            </span>
            {t("reports.historyClear")}
          </button>
        )}

        {groups.length > 1 && (
          <button
            type="button"
            onClick={toggleAllMonths}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={allCollapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"} />
            </span>
            {allCollapsed ? t("reports.historyExpandAll") : t("reports.historyCollapseAll")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-4 space-y-2">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-11 w-full animate-pulse rounded-md bg-background-100" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-history-line text-xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">{t("reports.historyEmpty")}</p>
          <p className="text-xs text-foreground-500">{t("reports.historyEmptyDesc")}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-filter-off-line text-xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">{t("reports.historyNoMatch")}</p>
          <p className="text-xs text-foreground-500">{t("reports.historyNoMatchDesc")}</p>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          {groups.map((group) => {
            const isCollapsed = collapsed.includes(group.key);
            return (
              <div key={group.key} className="overflow-hidden rounded-md border border-background-200">
                <button
                  type="button"
                  onClick={() => toggleMonth(group.key)}
                  className="flex w-full cursor-pointer items-center justify-between gap-3 bg-background-100 px-4 py-3 text-left transition-colors hover:bg-background-200/70"
                >
                  <span className="flex items-center gap-2">
                    <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                      <i className={isCollapsed ? "ri-add-line" : "ri-subtract-line"} />
                    </span>
                    <span className="font-heading text-sm font-semibold text-foreground-950">
                      {formatMonth(group.key)}
                    </span>
                  </span>
                  <span className="shrink-0 text-[11px] text-foreground-500">
                    {t("reports.historyMonthSummary", { count: group.tasks.length, rows: group.rows })}
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-sm">
                      <thead>
                        <tr className="bg-background-50 text-left text-xs font-medium text-foreground-600">
                          <th className="px-4 py-3">{t("reports.colTime")}</th>
                          <th className="px-4 py-3">{t("reports.colExporter")}</th>
                          <th className="px-4 py-3">{t("reports.colReport")}</th>
                          <th className="px-4 py-3">{t("reports.colCategory")}</th>
                          <th className="px-4 py-3 text-right">{t("reports.colRows")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.tasks.map((task) => (
                          <tr key={task.id} className="border-t border-background-200">
                            <td className="px-4 py-3">
                              <p className="text-xs text-foreground-900">{formatFullDate(task.created_at)}</p>
                              <p className="text-[11px] text-foreground-400">{formatRelativeTime(task.created_at)}</p>
                            </td>
                            <td className="px-4 py-3 text-xs text-foreground-800">
                              {task.exported_by_name || t("reports.unknown")}
                            </td>
                            <td className="px-4 py-3 text-xs text-foreground-900">
                              <span className="flex flex-wrap items-center gap-2">
                                <span>{task.report_name}</span>
                                {task.window_label && (
                                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
                                    <i className="ri-filter-3-line" />
                                    {t("reports.historyWindowTag", { window: task.window_label })}
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                                  CATEGORY_TONE[task.category || ""] ?? "bg-background-200 text-foreground-700"
                                }`}
                              >
                                {task.category ? catLabel(task.category) : t("reports.others")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-xs font-medium text-foreground-900">
                              {task.row_count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}